import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { flattenNavDestinations, formatNavGoChord } from '../../utils/sidebarMenu.js';

const NavJumpPalette = ({ open, onClose, user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const destinations = useMemo(() => flattenNavDestinations(user), [user]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const mapped = destinations.map((row) => {
      const name = row.label || (row.labelKey ? t(row.labelKey) : row.path);
      const parent = row.parentLabelKey ? t(row.parentLabelKey) : '';
      const label = parent && !row.primary ? `${parent} · ${name}` : name;
      return { ...row, label };
    });
    if (!q) return mapped;
    return mapped.filter((row) =>
      `${row.label} ${row.path}`.toLowerCase().includes(q),
    );
  }, [destinations, query, t]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setActive(0);
    const timer = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  if (!open) return null;

  const go = (row) => {
    if (!row?.path) return;
    onClose();
    navigate(row.path);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(rows.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      go(rows[active]);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center p-3 sm:p-10 bg-black/40"
      data-app-modal="true"
      data-nav-jump="true"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nav-jump-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Jump to a page you can access"
            className="flex-1 min-w-0 text-sm py-1.5 outline-none"
            aria-labelledby="nav-jump-title"
          />
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:bg-gray-100"
            aria-label="Close jump list"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p id="nav-jump-title" className="sr-only">Jump to page</p>
        <ul className="max-h-[60vh] overflow-y-auto py-1">
          {rows.length === 0 ? (
            <li className="px-3 py-3 text-sm text-gray-400">No matching page</li>
          ) : (
            rows.map((row, idx) => (
              <li key={row.id}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => go(row)}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${
                    idx === active ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-800 truncate">{row.label}</span>
                    <span className="block text-[11px] text-gray-400 truncate">{row.path}</span>
                  </span>
                  {row.goKey ? (
                    <kbd className="shrink-0 px-1.5 py-0.5 rounded-md border border-gray-200 bg-white text-[10px] font-mono text-gray-600">
                      {formatNavGoChord(row.goKey)}
                    </kbd>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

export default NavJumpPalette;
